
import { Helmet } from "react-helmet";
import type { EventWithImages } from "@/hooks/useGalleryEvents";
import { localizePath } from "@/utils/languageRoutes";

interface GallerySEOProps {
  language: string;
  events: EventWithImages[];
}

const ORGANIZATION_SCHEMA = {
  '@type': 'Organization',
  name: 'Duru Compliance',
  url: 'https://durucompliance.com',
};

const getPostalAddress = (location?: string | null) => {
  const fallbackLocation = 'Bengaluru, India';
  const locationParts = (location || fallbackLocation).split(',').map((part) => part.trim()).filter(Boolean);
  const addressCountry = locationParts.at(-1) || 'India';
  const addressLocality = locationParts.length > 1 ? locationParts.slice(0, -1).join(', ') : locationParts[0] || 'Bengaluru';

  return {
    '@type': 'PostalAddress',
    addressLocality,
    addressCountry,
  };
};

const GallerySEO = ({ language, events }: GallerySEOProps) => {
  const featuredEvent = events.find(event => event.is_featured);
  const baseUrl = 'https://durucompliance.com';
  const defaultImage = featuredEvent?.coverImage?.image_url || `${baseUrl}/og-image.png`;
  const englishUrl = `${baseUrl}${localizePath('/gallery', 'EN')}`;
  const koreanUrl = `${baseUrl}${localizePath('/gallery', 'KO')}`;
  const currentUrl = language === 'KO' ? koreanUrl : englishUrl;
  const title = language === 'KO'
    ? '두루컴플라이언스 갤러리 - 비즈니스 이벤트 및 글로벌 활동'
    : 'Duru Compliance Gallery - Business Events & Global Presence';
  const description = language === 'KO'
    ? '두루컴플라이언스의 비즈니스 서밋, 파트너십, 기업 이정표를 통한 여정을 살펴보세요. 인도에서의 글로벌 확장 스토리.'
    : 'Explore Duru Compliance\'s journey through business summits, partnerships, and corporate milestones. Our global expansion story in India.';
  const shortDescription = language === 'KO'
    ? '두루컴플라이언스의 비즈니스 서밋, 파트너십, 기업 이정표를 통한 여정을 살펴보세요.'
    : 'Explore Duru Compliance\'s journey through business summits, partnerships, and corporate milestones.';
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: title,
        description,
        url: currentUrl,
        image: defaultImage,
        publisher: { '@type': 'Organization', name: 'Duru Compliance', url: baseUrl },
        inLanguage: language === 'KO' ? 'ko' : 'en',
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: events.length,
          itemListElement: events.map((event, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Event',
              '@id': `${currentUrl}#event-${event.id}`,
              name: language === 'KO' && event.title_ko ? event.title_ko : event.title_en,
              description: language === 'KO' && event.description_ko ? event.description_ko : event.description_en,
              image: event.coverImage?.image_url || defaultImage,
              url: currentUrl,
              startDate: event.event_date,
              endDate: event.event_date,
              eventStatus: 'https://schema.org/EventScheduled',
              eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
              isAccessibleForFree: true,
              location: {
                '@type': 'Place',
                name: event.location || 'Duru Compliance',
                address: getPostalAddress(event.location),
              },
              organizer: ORGANIZATION_SCHEMA,
              performer: ORGANIZATION_SCHEMA,
              offers: {
                '@type': 'Offer',
                url: currentUrl,
                price: '0',
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
                validFrom: event.created_at || event.event_date,
              },
            },
          })),
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: language === 'KO' ? '홈' : 'Home', item: language === 'KO' ? `${baseUrl}/ko` : `${baseUrl}/en` },
          { '@type': 'ListItem', position: 2, name: language === 'KO' ? '갤러리' : 'Gallery', item: currentUrl },
        ],
      },
    ],
  };

  return (
    <Helmet htmlAttributes={{ lang: language === 'KO' ? 'ko' : 'en' }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      
      {/* OpenGraph Tags */}
      <meta property="og:type" content="website" />
      <meta
        property="og:title"
        content={title}
      />
      <meta
        property="og:description"
        content={shortDescription}
      />
      <meta property="og:image" content={defaultImage} />
      <meta property="og:image:alt" content="Duru Compliance Gallery" />
      
      {/* Language Alternates */}
      <link rel="alternate" hrefLang="en" href={englishUrl} />
      <link rel="alternate" hrefLang="ko" href={koreanUrl} />
      <link rel="alternate" hrefLang="x-default" href={englishUrl} />
      <link rel="canonical" href={currentUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default GallerySEO;
